import { Injectable } from '@angular/core';
import Dexie, { Table } from 'dexie';
import {
    CDFT,
    CDFTTable,
    StringEquipment,
    WellStringStatus,
    OptionItem,
} from 'app/modules/cdft/model/cdft.model';
import { PaginateResponse } from 'app/model';

/**
 * Offline Database Service using Dexie and IndexedDB
 * Stores CDFT module data with relationships by cdftId and stringId
 */
export class CDFTOfflineDb extends Dexie {
    // Tables
    cdftTables!: Table<ICDFTTable>;
    cdftDetails!: Table<ICDFT>;
    stringEquipment!: Table<IStringEquipment>;
    wellStringStatus!: Table<IWellStringStatus>;
    valveOptionTypes!: Table<IValveOptionType>;
    sssvOptionTypes!: Table<ISSVOptionType>;
    wellStrings!: Table<IWellString>;
    syncQueue!: Table<ISyncQueueItem>;

    constructor() {
        super('CDFTOfflineDb');
        this.version(1).stores({
            // Define primary keys and indexes
            // Use 'id' as primary key for cdftTables and cdftDetails (comes from API)
            cdftTables: 'id, wellStringId',
            cdftDetails: 'id, wellStringId',
            stringEquipment: 'wellStringId',
            wellStringStatus: 'wellStringId',
            valveOptionTypes: '++id',
            sssvOptionTypes: '++id',
            wellStrings: 'id, wellStringId',
            syncQueue: '++id, cdftId, stringId, timestamp',
        });
    }
}

/**
 * Interfaces for offline database tables
 */
export interface ICDFTTable extends CDFTTable {
    orderIndex?: number; // Preserve original API response order
    syncedAt?: number;
}

export interface ICDFT extends CDFT {
    syncedAt?: number;
}

export interface IStringEquipment extends StringEquipment {
    syncedAt?: number;
}

export interface IWellStringStatus extends WellStringStatus {
    syncedAt?: number;
}

export interface IValveOptionType {
    id: string;
    items: OptionItem[];
    syncedAt?: number;
}

export interface ISSVOptionType {
    id: string;
    items: OptionItem[];
    syncedAt?: number;
}

export interface IWellString {
    id: string;
    wellStringId: string;
    [key: string]: any;
    syncedAt?: number;
}

export interface ISyncQueueItem {
    id?: number;
    cdftId?: string;
    wellName?: string;
    stringId?: string;
    apiEndpoint: string;
    method: 'GET' | 'POST' | 'PUT' | 'DELETE';
    payload?: any;
    timestamp: number;
    retries: number;
    lastError?: string;
}

/**
 * CDFT Offline Service
 * Manages data sync between API and IndexedDB
 */
@Injectable({ providedIn: 'root' })
export class CDFTOfflineService {
    private readonly db: CDFTOfflineDb;

    constructor() {
        this.db = new CDFTOfflineDb();
    }

    // ==================== CDFT Table Methods ====================

    async saveCDFTTable(data: PaginateResponse<CDFTTable>): Promise<void> {
        try {
            // Clear existing data or update with new data
            if (data.data && Array.isArray(data.data)) {
                await this.db.cdftTables.bulkPut(
                    data.data.map((item, index) => ({
                        ...item,
                        orderIndex: index, // Preserve original order from API
                        syncedAt: Date.now(),
                    })),
                );
            }
        } catch (error) {
            console.error('Error saving CDFT table data:', error);
            throw error;
        }
    }

    async getCDFTTable(wellStringId?: string): Promise<ICDFTTable[]> {
        try {
            let results: ICDFTTable[];
            if (wellStringId) {
                results = await this.db.cdftTables
                    .where('wellStringId')
                    .equals(wellStringId)
                    .toArray();
            } else {
                results = await this.db.cdftTables.toArray();
            }
            // Sort by orderIndex to maintain original API response order
            return results.sort(
                (a, b) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0),
            );
        } catch (error) {
            console.error('Error retrieving CDFT table data:', error);
            return [];
        }
    }

    // ==================== CDFT Detail Methods ====================

    async saveCDFTDetail(cdft: CDFT): Promise<void> {
        try {
            console.log('Saving CDFT detail to cache:', cdft.id, cdft);
            const dataToSave = {
                ...cdft,
                syncedAt: Date.now(),
            };
            const result = await this.db.cdftDetails.put(dataToSave);
            console.log('Successfully saved CDFT detail:', result);
        } catch (error) {
            console.error('Error saving CDFT detail:', error);
            throw error;
        }
    }

    async getCDFTDetail(cdftId: string): Promise<ICDFT | undefined> {
        try {
            console.log('Retrieving CDFT detail from cache:', cdftId);
            // Search by cdftId which is now the primary key
            const result = await this.db.cdftDetails.get(cdftId);
            console.log('Retrieved CDFT detail from cache:', cdftId, result);
            if (!result) {
                console.warn(
                    `CDFT detail not found in cache for ID: ${cdftId}`,
                );
                // Log all keys in the database for debugging
                const allKeys = await this.db.cdftDetails.toCollection().keys();
                console.warn('Available CDFT detail keys in cache:', allKeys);
                return undefined;
            }
            return result;
        } catch (error) {
            console.error('Error retrieving CDFT detail from cache:', error);
            return undefined;
        }
    }

    async deleteCDFTDetail(cdftId: string): Promise<void> {
        try {
            await this.db.cdftDetails.delete(cdftId);
        } catch (error) {
            console.error('Error deleting CDFT detail:', error);
            throw error;
        }
    }

    // ==================== String Equipment Methods ====================

    async saveStringEquipment(
        stringId: string,
        data: StringEquipment,
    ): Promise<void> {
        try {
            await this.db.stringEquipment.put({
                ...data,
                wellStringId: stringId,
                syncedAt: Date.now(),
            });
        } catch (error) {
            console.error('Error saving string equipment:', error);
            throw error;
        }
    }

    async getStringEquipment(
        stringId: string,
    ): Promise<IStringEquipment | undefined> {
        try {
            return await this.db.stringEquipment
                .where('wellStringId')
                .equals(stringId)
                .first();
        } catch (error) {
            console.error('Error retrieving string equipment:', error);
            return undefined;
        }
    }

    // ==================== Well Production Category Methods ====================

    async saveWellStringStatus(status: WellStringStatus): Promise<void> {
        try {
            await this.db.wellStringStatus.put({
                ...status,
                syncedAt: Date.now(),
            });
        } catch (error) {
            console.error('Error saving well string status:', error);
            throw error;
        }
    }

    async getWellStringStatus(
        wellStringId: string,
    ): Promise<IWellStringStatus | undefined> {
        try {
            return await this.db.wellStringStatus
                .where('wellStringId')
                .equals(wellStringId)
                .first();
        } catch (error) {
            console.error('Error retrieving well string status:', error);
            return undefined;
        }
    }

    // ==================== Valve Option Type Methods ====================

    async saveValveOptionType(items: OptionItem[]): Promise<void> {
        try {
            await this.db.valveOptionTypes.put({
                id: 'valve-options',
                items,
                syncedAt: Date.now(),
            });
        } catch (error) {
            console.error('Error saving valve option types:', error);
            throw error;
        }
    }

    async getValveOptionType(): Promise<OptionItem[]> {
        try {
            const data = await this.db.valveOptionTypes
                .where('id')
                .equals('valve-options')
                .first();
            return data?.items || [];
        } catch (error) {
            console.error('Error retrieving valve option types:', error);
            return [];
        }
    }

    // ==================== SSSV Option Type Methods ====================

    async saveSSSVOptionType(items: OptionItem[]): Promise<void> {
        try {
            await this.db.sssvOptionTypes.put({
                id: 'sssv-options',
                items,
                syncedAt: Date.now(),
            });
        } catch (error) {
            console.error('Error saving SSSV option types:', error);
            throw error;
        }
    }

    async getSSSVOptionType(): Promise<OptionItem[]> {
        try {
            const data = await this.db.sssvOptionTypes
                .where('id')
                .equals('sssv-options')
                .first();
            return data?.items || [];
        } catch (error) {
            console.error('Error retrieving SSSV option types:', error);
            return [];
        }
    }

    // ==================== Well String Methods ====================

    async saveWellString(wellString: WellString): Promise<void> {
        try {
            await this.db.wellStrings.put({
                id: wellString.id,
                wellStringId: wellString.id,
                ...wellString,
                syncedAt: Date.now(),
            });
        } catch (error) {
            console.error('Error saving well string:', error);
            throw error;
        }
    }

    async getWellString(id: string): Promise<IWellString | undefined> {
        try {
            return await this.db.wellStrings.where('id').equals(id).first();
        } catch (error) {
            console.error('Error retrieving well string:', error);
            return undefined;
        }
    }

    // ==================== Sync Queue Methods ====================

    /**
     * Convert FormData to plain object for IndexedDB storage
     * FormData cannot be cloned directly by IndexedDB
     */
    private serializePayload(payload: any): any {
        if (payload instanceof FormData) {
            const obj: any = {};
            const entries = payload as any;

            // Handle FormData iteration
            if (typeof entries[Symbol.iterator] === 'function') {
                for (const [key, value] of entries) {
                    if (
                        typeof value === 'object' &&
                        value !== null &&
                        ('slice' in value || 'stream' in value)
                    ) {
                        // File or Blob
                        const fileValue = value as unknown as {
                            name?: string;
                            size?: number;
                            type?: string;
                        };
                        obj[key] = {
                            type: 'file',
                            name: fileValue.name || 'blob',
                            size: fileValue.size,
                            mimeType: fileValue.type,
                        };
                    } else {
                        obj[key] = value;
                    }
                }
            }
            return obj;
        }
        return payload;
    }

    async addToSyncQueue(
        cdftId: string | undefined,
        wellName: string | undefined,
        stringId: string | undefined,
        apiEndpoint: string,
        method: 'GET' | 'POST' | 'PUT' | 'DELETE',
        payload?: any,
    ): Promise<void> {
        try {
            // Serialize FormData to plain object for IndexedDB storage
            const serializedPayload = this.serializePayload(payload);

            await this.db.syncQueue.add({
                cdftId,
                wellName,
                stringId,
                apiEndpoint,
                method,
                payload: serializedPayload,
                timestamp: Date.now(),
                retries: 0,
            });
        } catch (error) {
            console.error('Error adding to sync queue:', error);
            throw error;
        }
    }

    async getSyncQueue(): Promise<ISyncQueueItem[]> {
        try {
            return await this.db.syncQueue.toArray();
        } catch (error) {
            console.error('Error retrieving sync queue:', error);
            return [];
        }
    }

    /**
     * Get latest sync queue item for a specific CDFT ID
     * Returns only the most recent update for each CDFT
     */
    async getLatestSyncQueueByCdftId(
        cdftId: string,
    ): Promise<ISyncQueueItem | undefined> {
        try {
            const items = await this.db.syncQueue
                .where('cdftId')
                .equals(cdftId)
                .toArray();

            if (items.length === 0) return undefined;

            // Return the most recent item (highest timestamp)
            return items.reduce(
                (latest, current) =>
                    current.timestamp > latest.timestamp ? current : latest,
                items[0],
            );
        } catch (error) {
            console.error('Error retrieving latest sync queue item:', error);
            return undefined;
        }
    }

    async removeSyncQueueItem(id: number): Promise<void> {
        try {
            await this.db.syncQueue.delete(id);
        } catch (error) {
            console.error('Error removing sync queue item:', error);
            throw error;
        }
    }

    async updateSyncQueueItemError(id: number, error: string): Promise<void> {
        try {
            await this.db.syncQueue.update(id, {
                lastError: error,
                retries: Dexie.liveQuery(() =>
                    this.db.syncQueue.where('id').equals(id).first(),
                ) as any,
            });
        } catch (error) {
            console.error('Error updating sync queue item:', error);
            throw error;
        }
    }

    /**
     * Get count of pending sync queue items
     */
    async getSyncQueueCount(): Promise<number> {
        try {
            return await this.db.syncQueue.count();
        } catch (error) {
            console.error('Error getting sync queue count:', error);
            return 0;
        }
    }

    /**
     * Get sync queue items grouped by CDFT ID
     * Returns only the latest item for each CDFT
     */
    async getSyncQueueGrouped(): Promise<ISyncQueueItem[]> {
        try {
            const allItems = await this.db.syncQueue.toArray();
            const grouped = new Map<string, ISyncQueueItem>();

            for (const item of allItems) {
                if (item.cdftId) {
                    const existing = grouped.get(item.cdftId);
                    if (!existing || item.timestamp > existing.timestamp) {
                        grouped.set(item.cdftId, item);
                    }
                }
            }

            return Array.from(grouped.values()).sort(
                (a, b) => b.timestamp - a.timestamp,
            );
        } catch (error) {
            console.error('Error getting grouped sync queue:', error);
            return [];
        }
    }

    async clearAllData(): Promise<void> {
        try {
            await this.db.delete();
            await this.db.open();
        } catch (error) {
            console.error('Error clearing database:', error);
            throw error;
        }
    }
}

export interface WellString {
    id: string;
    [key: string]: any;
}
